import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Link2, Package, Plus, Search, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useItems } from '@/features/inventory/hooks';
import {
  useEngProjectItems,
  useLinkEngProjectItem,
  useUnlinkEngProjectItem,
  type EngProjectItem,
} from '@/features/engineering/hooks';
import {
  ENG_ITEM_RELATIONSHIP_META,
  ENG_ITEM_RELATIONSHIP_OPTIONS,
  type EngItemRelationship,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { cn } from '@/lib/utils';

/**
 * Production tie-in: the catalog items this project designs, uses or
 * replaces. The reverse view (project links on the item page) lives in
 * `ItemEngineeringCard`.
 */
export function EngItemLinksCard({
  projectId,
  canEdit,
  className,
}: {
  projectId: string;
  canEdit: boolean;
  className?: string;
}) {
  const { data: links, isLoading } = useEngProjectItems(projectId);
  const unlink = useUnlinkEngProjectItem();
  const [open, setOpen] = useState(false);

  const handleUnlink = async (link: EngProjectItem) => {
    if (!window.confirm(`Remove the link to ${link.item?.sku ?? 'this item'}?`)) return;
    try {
      await unlink.mutateAsync({ projectId, itemId: link.item_id });
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-4 w-4 text-info" />
            Catalog items
          </CardTitle>
          <CardDescription>
            Tie this design to the parts it produces so production can jump straight to the files.
          </CardDescription>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Link item
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (links ?? []).length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            No catalog items linked yet.
            {canEdit && ' Link the component this project engineers (e.g. “Bowl”).'}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {(links ?? []).map((link) => {
              const meta = ENG_ITEM_RELATIONSHIP_META[link.relationship];
              return (
                <li key={link.item_id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/catalog/items/${link.item_id}?tab=engineering`}
                        className="truncate text-sm font-medium text-foreground hover:underline"
                      >
                        {link.item?.name ?? 'Item'}
                      </Link>
                      <span className="font-mono text-xs text-muted-foreground">{link.item?.sku}</span>
                      <Badge variant={meta.variant} className="text-[11px]">
                        {meta.label}
                      </Badge>
                    </div>
                    {link.note && <p className="mt-0.5 truncate text-xs text-muted-foreground">{link.note}</p>}
                  </div>
                  <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0" aria-label="Open item">
                    <Link to={`/catalog/items/${link.item_id}?tab=engineering`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => void handleUnlink(link)}
                      aria-label="Unlink item"
                    >
                      <Unlink className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      <LinkItemDialog
        open={open}
        onOpenChange={setOpen}
        projectId={projectId}
        alreadyLinked={new Set((links ?? []).map((l) => l.item_id))}
      />
    </Card>
  );
}

export function LinkItemDialog({
  open,
  onOpenChange,
  projectId,
  alreadyLinked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  alreadyLinked: Set<string>;
}) {
  const { data: items, isLoading } = useItems();
  const link = useLinkEngProjectItem();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [relationship, setRelationship] = useState<EngItemRelationship>('designs');
  const [note, setNote] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (items ?? []).filter((i) => i.status !== 'archived');
    const matched = q
      ? list.filter((i) => `${i.sku} ${i.name}`.toLowerCase().includes(q))
      : list;
    return matched.slice(0, 40);
  }, [items, query]);

  const reset = () => {
    setQuery('');
    setSelected(null);
    setRelationship('designs');
    setNote('');
  };

  const submit = async () => {
    if (!selected) {
      toast.error('Pick an item first.');
      return;
    }
    try {
      await link.mutateAsync({ projectId, itemId: selected, relationship, note });
      toast.success('Item linked.');
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatError(err, 'Could not link the item.'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Link a catalog item
          </DialogTitle>
          <DialogDescription>
            The item’s page gets an Engineering tab pointing back to this project and its files.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by SKU or name…"
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto rounded-lg border border-border">
            {isLoading ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading items…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No items match.</p>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((item) => {
                  const linked = alreadyLinked.has(item.id);
                  const active = selected === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        disabled={linked}
                        onClick={() => setSelected(item.id)}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                          active ? 'bg-info-soft/30' : 'hover:bg-secondary/60',
                          linked && 'cursor-not-allowed opacity-50',
                        )}
                      >
                        <span className="font-mono text-xs text-muted-foreground">{item.sku}</span>
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        {linked && <span className="text-xs text-muted-foreground">Linked</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Relationship</Label>
              <Select value={relationship} onValueChange={(v) => setRelationship(v as EngItemRelationship)}>
                <SelectTrigger aria-label="Relationship">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENG_ITEM_RELATIONSHIP_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{ENG_ITEM_RELATIONSHIP_META[relationship].hint}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eng-link-note">Note (optional)</Label>
              <Input
                id="eng-link-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. rev B supersedes the molded version"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={link.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={link.isPending || !selected}>
            {link.isPending ? 'Linking…' : 'Link item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
